/**
 * My Commissions service.
 *
 * Thin wrapper over the self-scoped `get_my_commission_report` RPC. The function
 * is SECURITY DEFINER and returns ONLY the caller's own LO-visible records, so
 * there is no client-side scoping to get wrong here.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import {
  type MyCommissionReport,
  type AdminCommissionReport
} from '@/components/reports/myCommissionReportModel';

const log = createComponentLogger('myCommissionService', 'reports');

const EMPTY_REPORT: MyCommissionReport = { plan: null, earnings: [], context: [] };
const EMPTY_ADMIN_REPORT: AdminCommissionReport = { report_users: [], earnings: [], context: [] };

/**
 * Fetch the caller's own commissions/payouts for [fromIso, toIso] (YYYY-MM-DD),
 * filtered by fund_date. Throws on RPC error.
 */
export async function getMyCommissionReport(
  fromIso: string,
  toIso: string
): Promise<MyCommissionReport> {
  const { data, error } = await supabase.rpc('get_my_commission_report', {
    p_from: fromIso,
    p_to: toIso
  });

  if (error) {
    log.error('get_my_commission_report failed', { metadata: { fromIso, toIso } }, error);
    throw error;
  }

  const result = (data ?? EMPTY_REPORT) as MyCommissionReport;
  return {
    plan: result.plan ?? null,
    earnings: result.earnings ?? [],
    // Splits an LO paid out to others are confidential. The self RPC already
    // returns none; force [] here too so the LO path never carries splits even
    // if the RPC response regresses.
    context: [],
    bonuses: (result.bonuses ?? []).map(({ history: _history, ...bonus }) => bonus),
    volume_bonus_activity: result.volume_bonus_activity ?? []
  };
}

/**
 * Admin-only: ALL commission records for the org in [fromIso, toIso], plus the
 * selectable/roster user set. The RPC enforces the strict admin gate and RAISEs
 * (surfaced here as a thrown error) for non-admins.
 */
export async function getCommissionReportAll(
  fromIso: string,
  toIso: string
): Promise<AdminCommissionReport> {
  const { data, error } = await supabase.rpc('get_commission_report_all', {
    p_from: fromIso,
    p_to: toIso
  });

  if (error) {
    log.error('get_commission_report_all failed', { metadata: { fromIso, toIso } }, error);
    throw error;
  }

  const result = (data ?? EMPTY_ADMIN_REPORT) as AdminCommissionReport;
  return {
    report_users: result.report_users ?? [],
    earnings: result.earnings ?? [],
    context: result.context ?? [],
    bonuses: result.bonuses ?? [],
    bonus_owner: result.bonus_owner === true,
    bonus_entry_enabled: result.bonus_entry_enabled === true,
    bonus_current_month: result.bonus_current_month,
    volume_bonus_activity: result.volume_bonus_activity ?? []
  };
}

/**
 * One LO's lifetime "available profitability" for the Banker Recon display line.
 * Uses reset-adjusted stored nets minus posted recapture deductions from a
 * one-LO, Banker-Recon-gated RPC. Unknown historical nets fail closed. Excludes
 * the recon being edited. Returns null when the helper is unavailable/denied
 * (caller renders "Unavailable"); 0 is a valid empty result, not unavailable.
 */
export async function getLoAvailableProfitability(
  loUserId: string,
  excludeReconId: string | null
): Promise<number | null> {
  if (!loUserId) {
    return null;
  }
  const { data, error } = await supabase.rpc('get_lo_available_profitability_rows', {
    p_lo_user_id: loUserId,
    p_exclude_banker_recon_id: excludeReconId
  });

  if (error) {
    log.warn('get_lo_available_profitability_rows failed', { metadata: { loUserId } }, error);
    return null;
  }

  const result = data as { profitability?: { complete?: boolean; available?: unknown } } | null;
  const available = result?.profitability?.available;
  return result?.profitability?.complete === true && typeof available === 'number' && Number.isFinite(available)
    ? available : null;
}
